/**
 * WalletvenPrivacy.jsx — Política de Privacidad / Privacy Policy (ES/EN)
 * -------------------------------------------------------------------------
 * DEV NOTES (Muhammad):
 * 1. Fill in the COMPANY object below with Walletven's real legal details.
 *    Everything in [square brackets] is a placeholder to replace.
 * 2. This is a template to the structure a regulated crypto on-ramp needs.
 *    It is NOT legal advice — have the client's lawyer/DPO review it before
 *    going live, especially data-collection, retention and rights sections.
 * 3. CryptoPocket (Fintech Payments PSC, S.L.) is listed as an external
 *    payment/settlement processor, which is accurate for the integration.
 * 4. Keep the COMPANY object in sync with WalletvenTerms.jsx (or extract it
 *    into a shared module and import it into both).
 */

import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";

const COMPANY = {
  brandName: "Walletven",
  legalName: "Walletven — JMS Intermedia SLU",
  jurisdiction: "Madrid",
  address: "Garcilaso de la Vega 21, Local 12, 38005, Santa Cruz de Tenerife, Islas Canarias, España.",
  contactEmail: "hola@walletven.com",
  privacyEmail: "hola@walletven.com",
  website: "walletven.com",
  effectiveDate: "[01/04/2026]",
};

const fill = (s) =>
  s
    .replaceAll("%ENTITY%", COMPANY.legalName)
    .replaceAll("%BRAND%", COMPANY.brandName)
    .replaceAll("%JURIS%", COMPANY.jurisdiction)
    .replaceAll("%ADDRESS%", COMPANY.address)
    .replaceAll("%EMAIL%", COMPANY.contactEmail)
    .replaceAll("%PRIVEMAIL%", COMPANY.privacyEmail)
    .replaceAll("%SITE%", COMPANY.website)
    .replaceAll("%DATE%", COMPANY.effectiveDate);

const UI = {
  es: {
    docLabel: "Documento legal",
    title: "Política de Privacidad de Datos",
    updated: "Última actualización",
    toc: "Contenido",
    top: "Volver arriba",
    langAria: "Cambiar idioma",
  },
  en: {
    docLabel: "Legal document",
    title: "Data Privacy Policy",
    updated: "Last updated",
    toc: "Contents",
    top: "Back to top",
    langAria: "Change language",
  },
};

const SECTIONS = [
  {
    id: "intro",
    es: {
      title: "Introducción",
      body: [
        "En %ENTITY% («%BRAND%», «nosotros») nos comprometemos a proteger su privacidad. Esta Política de Privacidad explica qué datos personales recopilamos, con qué finalidad los tratamos y qué derechos le asisten cuando utiliza la plataforma %BRAND% en %SITE%.",
        "Le recomendamos leer esta Política junto con nuestros Términos y Condiciones y cualquier aviso específico que le facilitemos al recabar sus datos.",
      ],
    },
    en: {
      title: "Introduction",
      body: [
        "At %ENTITY% (\u201C%BRAND%\u201D, \u201Cwe\u201D) we are committed to protecting your privacy. This Privacy Policy explains what personal data we collect, why we process it, and what rights you have when you use the %BRAND% platform at %SITE%.",
        "We recommend reading this Policy together with our Terms and Conditions and any specific notice we provide when collecting your data.",
      ],
    },
  },
  {
    id: "controller",
    es: {
      title: "Responsable del tratamiento",
      body: [
        "El responsable del tratamiento de sus datos personales es %ENTITY%, con domicilio en %ADDRESS%. Para cualquier cuestión relacionada con la protección de datos puede escribirnos a %PRIVEMAIL%.",
      ],
    },
    en: {
      title: "Data controller",
      body: [
        "The controller of your personal data is %ENTITY%, with its registered office at %ADDRESS%. For any matter relating to data protection you can write to us at %PRIVEMAIL%.",
      ],
    },
  },
  {
    id: "data",
    es: {
      title: "Datos que recopilamos",
      body: [
        "Datos de identificación y contacto: nombre, apellidos, fecha de nacimiento, nacionalidad, dirección, correo electrónico y número de teléfono.",
        "Datos de verificación (KYC): documento de identidad, fotografías o pruebas de vida y demás información exigida por la normativa de prevención del blanqueo de capitales.",
        "Datos de transacciones: importes, monedas, direcciones de cartera, historial de operaciones y método de pago.",
        "Datos técnicos: dirección IP, tipo de dispositivo y navegador, identificadores y datos de uso de la Plataforma.",
      ],
    },
    en: {
      title: "Data we collect",
      body: [
        "Identification and contact data: first name, surname, date of birth, nationality, address, email address, and phone number.",
        "Verification data (KYC): identity document, photographs or liveness checks, and other information required by anti-money-laundering rules.",
        "Transaction data: amounts, currencies, wallet addresses, transaction history, and payment method.",
        "Technical data: IP address, device and browser type, identifiers, and Platform usage data.",
      ],
    },
  },
  {
    id: "sources",
    es: {
      title: "Cómo recopilamos sus datos",
      body: [
        "Recopilamos datos que usted nos facilita directamente al registrarse o utilizar la Plataforma, datos generados automáticamente durante su uso, y datos procedentes de terceros, como proveedores de verificación de identidad y fuentes de prevención del fraude.",
      ],
    },
    en: {
      title: "How we collect your data",
      body: [
        "We collect data you provide directly when registering for or using the Platform, data generated automatically during use, and data from third parties such as identity-verification providers and fraud-prevention sources.",
      ],
    },
  },
  {
    id: "purposes",
    es: {
      title: "Finalidades y bases legales",
      body: [
        "Prestación de los servicios y ejecución del contrato: gestionar su Cuenta y procesar sus operaciones.",
        "Cumplimiento de obligaciones legales: verificación de identidad (KYC), prevención del blanqueo de capitales y de la financiación del terrorismo, y obligaciones fiscales y contables.",
        "Interés legítimo: seguridad de la Plataforma, prevención del fraude y mejora de nuestros servicios.",
        "Consentimiento: comunicaciones comerciales, cuando proceda, que usted podrá retirar en cualquier momento.",
      ],
    },
    en: {
      title: "Purposes and legal bases",
      body: [
        "Provision of the services and performance of the contract: to manage your Account and process your transactions.",
        "Compliance with legal obligations: identity verification (KYC), prevention of money laundering and terrorist financing, and tax and accounting obligations.",
        "Legitimate interest: Platform security, fraud prevention, and improvement of our services.",
        "Consent: marketing communications, where applicable, which you may withdraw at any time.",
      ],
    },
  },
  {
    id: "kyc",
    es: {
      title: "Verificación de identidad y prevención del blanqueo",
      body: [
        "Como proveedor de servicios relacionados con criptoactivos, estamos obligados a verificar la identidad de nuestros Usuarios y a supervisar las operaciones para detectar actividades sospechosas. Para ello podemos tratar sus datos y compartirlos con proveedores de verificación y con las autoridades competentes cuando la ley lo exija.",
      ],
    },
    en: {
      title: "Identity verification and AML",
      body: [
        "As a provider of crypto-asset-related services, we are required to verify the identity of our Users and to monitor transactions to detect suspicious activity. To do so we may process your data and share it with verification providers and with competent authorities where the law requires.",
      ],
    },
  },
  {
    id: "sharing",
    es: {
      title: "Con quién compartimos sus datos",
      body: [
        "Proveedores de servicios de pago y liquidación, incluido CryptoPocket (Fintech Payments PSC, S.L.), para procesar y liquidar sus operaciones.",
        "Proveedores de verificación de identidad, prevención del fraude y servicios tecnológicos que actúan como encargados del tratamiento por cuenta nuestra.",
        "Autoridades públicas, reguladores y órganos judiciales cuando exista una obligación legal o sea necesario para proteger nuestros derechos.",
        "No vendemos sus datos personales a terceros.",
      ],
    },
    en: {
      title: "Who we share your data with",
      body: [
        "Payment and settlement providers, including CryptoPocket (Fintech Payments PSC, S.L.), to process and settle your transactions.",
        "Identity-verification, fraud-prevention, and technology providers acting as processors on our behalf.",
        "Public authorities, regulators, and courts where there is a legal obligation or it is necessary to protect our rights.",
        "We do not sell your personal data to third parties.",
      ],
    },
  },
  {
    id: "transfers",
    es: {
      title: "Transferencias internacionales",
      body: [
        "Algunos de nuestros proveedores pueden estar ubicados fuera de su país de residencia. Cuando transfiramos datos internacionalmente, adoptaremos las garantías adecuadas exigidas por la normativa aplicable, como cláusulas contractuales tipo.",
      ],
    },
    en: {
      title: "International transfers",
      body: [
        "Some of our providers may be located outside your country of residence. When we transfer data internationally, we apply the appropriate safeguards required by applicable law, such as standard contractual clauses.",
      ],
    },
  },
  {
    id: "retention",
    es: {
      title: "Conservación de los datos",
      body: [
        "Conservaremos sus datos personales durante el tiempo necesario para prestar los servicios y, posteriormente, durante los plazos exigidos por la normativa aplicable, en particular la normativa de prevención del blanqueo de capitales, que puede requerir la conservación de determinada información durante varios años tras el fin de la relación.",
      ],
    },
    en: {
      title: "Data retention",
      body: [
        "We retain your personal data for as long as necessary to provide the services and, afterwards, for the periods required by applicable law, in particular anti-money-laundering rules, which may require certain information to be kept for several years after the relationship ends.",
      ],
    },
  },
  {
    id: "security",
    es: {
      title: "Seguridad",
      body: [
        "Aplicamos medidas técnicas y organizativas apropiadas para proteger sus datos frente a accesos no autorizados, pérdida o alteración. No obstante, ninguna transmisión por internet es completamente segura y no podemos garantizar una seguridad absoluta.",
      ],
    },
    en: {
      title: "Security",
      body: [
        "We apply appropriate technical and organisational measures to protect your data against unauthorised access, loss, or alteration. However, no transmission over the internet is completely secure and we cannot guarantee absolute security.",
      ],
    },
  },
  {
    id: "cookies",
    es: {
      title: "Cookies y tecnologías similares",
      body: [
        "Utilizamos cookies y tecnologías similares para el funcionamiento de la Plataforma, para recordar sus preferencias y para analizar su uso. Puede gestionar sus preferencias de cookies a través de la configuración de su navegador o de nuestro panel de cookies, cuando esté disponible.",
      ],
    },
    en: {
      title: "Cookies and similar technologies",
      body: [
        "We use cookies and similar technologies to operate the Platform, remember your preferences, and analyse usage. You can manage your cookie preferences through your browser settings or our cookie panel, where available.",
      ],
    },
  },
  {
    id: "rights",
    es: {
      title: "Sus derechos",
      body: [
        "Usted tiene derecho a acceder a sus datos, solicitar su rectificación o supresión, oponerse o limitar su tratamiento, y solicitar la portabilidad de los mismos, en los términos previstos por la normativa aplicable.",
        "Para ejercer estos derechos, escríbanos a %PRIVEMAIL%. También tiene derecho a presentar una reclamación ante la autoridad de control competente en materia de protección de datos.",
      ],
    },
    en: {
      title: "Your rights",
      body: [
        "You have the right to access your data, request its rectification or erasure, object to or restrict its processing, and request its portability, on the terms provided by applicable law.",
        "To exercise these rights, write to us at %PRIVEMAIL%. You also have the right to lodge a complaint with the competent data-protection supervisory authority.",
      ],
    },
  },
  {
    id: "children",
    es: {
      title: "Menores de edad",
      body: [
        "La Plataforma no está dirigida a menores de edad y no recopilamos de forma consciente datos de personas que no tengan la mayoría de edad legal en su jurisdicción.",
      ],
    },
    en: {
      title: "Minors",
      body: [
        "The Platform is not directed at minors and we do not knowingly collect data from anyone who is not of legal age in their jurisdiction.",
      ],
    },
  },
  {
    id: "changes",
    es: {
      title: "Cambios en esta política",
      body: [
        "Podemos actualizar esta Política de Privacidad periódicamente. Publicaremos la versión vigente en la Plataforma indicando su fecha de entrada en vigor. Le recomendamos revisarla con regularidad.",
      ],
    },
    en: {
      title: "Changes to this policy",
      body: [
        "We may update this Privacy Policy from time to time. We will publish the current version on the Platform together with its effective date. We recommend reviewing it regularly.",
      ],
    },
  },
  {
    id: "contact",
    es: {
      title: "Contacto",
      body: [
        "Si tiene preguntas sobre esta Política o sobre el tratamiento de sus datos, contáctenos en %PRIVEMAIL% o en %ADDRESS%.",
      ],
    },
    en: {
      title: "Contact",
      body: [
        "If you have questions about this Policy or about how your data is processed, contact us at %PRIVEMAIL% or at %ADDRESS%.",
      ],
    },
  },
];

const styles = `
.wv-root{
  --wv-ink:#1a1a1f;
  --wv-bg:#ffffff;
  --wv-surface:#fff1f3;
  --wv-accent:#fc364e;
  --wv-accent-ink:#c8102e;
  --wv-border:#eae6e8;
  --wv-muted:#6b6b74;
  color:var(--wv-ink);
  background:var(--wv-bg);
  font-family:"Inter",ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  line-height:1.66;
  -webkit-font-smoothing:antialiased;
  scroll-behavior:smooth;
}
@media (prefers-reduced-motion: reduce){ .wv-root{scroll-behavior:auto;} }
.wv-root *{box-sizing:border-box;}
.wv-header{max-width:1220px;margin:0 auto;padding:56px 24px 28px;}
.wv-eyebrow{
  display:inline-block;font-size:.72rem;letter-spacing:.14em;text-transform:uppercase;
  font-weight:700;color:var(--wv-accent-ink);background:var(--wv-surface);
  border:1px solid var(--wv-border);padding:5px 11px;border-radius:999px;
}
.wv-brand{margin:18px 0 0;font-size:.95rem;font-weight:700;color:var(--wv-muted);letter-spacing:.02em;}
.wv-title{margin:6px 0 0;font-size:clamp(1.9rem,4.5vw,2.9rem);font-weight:800;letter-spacing:-.02em;line-height:1.1;}
.wv-meta{margin:14px 0 0;font-size:.9rem;color:var(--wv-muted);}
.wv-langbar{display:flex;gap:6px;margin:22px 0 0;}
.wv-langbtn{
  font:inherit;font-size:.82rem;font-weight:700;cursor:pointer;
  padding:7px 16px;border-radius:8px;border:1px solid var(--wv-border);
  background:#fff;color:var(--wv-muted);transition:all .15s ease;
}
.wv-langbtn:hover{border-color:var(--wv-accent);color:var(--wv-accent-ink);}
.wv-langbtn[aria-pressed="true"]{background:var(--wv-accent);border-color:var(--wv-accent);color:#fff;}
.wv-langbtn:focus-visible{outline:2px solid var(--wv-accent-ink);outline-offset:2px;}
.wv-rule{max-width:1220px;margin:22px auto 0;padding:0 24px;}
.wv-rule span{display:block;height:1px;background:var(--wv-border);}
.wv-shell{max-width:1220px;margin:0 auto;padding:8px 24px 96px;display:grid;grid-template-columns:1fr;gap:40px;}
@media (min-width:900px){ .wv-shell{grid-template-columns:248px 1fr;gap:56px;align-items:start;} }
.wv-toc{position:static;}
@media (min-width:900px){ .wv-toc{position:sticky;top:24px;} }
.wv-toc-title{font-size:.72rem;letter-spacing:.12em;text-transform:uppercase;font-weight:700;color:var(--wv-muted);margin:32px 0 12px;}
.wv-toc-list{list-style:none;margin:0;padding:0;border-left:2px solid var(--wv-border);}
.wv-toc-link{
  display:block;width:100%;text-align:left;font:inherit;font-size:.86rem;
  cursor:pointer;background:none;border:none;color:var(--wv-muted);
  padding:6px 0 6px 14px;margin-left:-2px;border-left:2px solid transparent;
  transition:color .15s ease,border-color .15s ease;
}
.wv-toc-link:hover{color:var(--wv-ink);}
.wv-toc-link[data-active="true"]{color:var(--wv-accent-ink);border-left-color:var(--wv-accent);font-weight:700;}
.wv-toc-link:focus-visible{outline:2px solid var(--wv-accent-ink);outline-offset:2px;border-radius:2px;}
.wv-content{min-width:0;padding-top:24px;}
.wv-section{scroll-margin-top:24px;padding:14px 0 26px;border-bottom:1px solid var(--wv-border);}
.wv-section:last-child{border-bottom:none;}
.wv-num{font-size:.78rem;font-weight:700;color:var(--wv-accent);letter-spacing:.04em;}
.wv-h2{margin:4px 0 12px;font-size:1.28rem;font-weight:750;letter-spacing:-.01em;}
.wv-p{margin:0 0 12px;max-width:70ch;color:#3b3b42;}
.wv-p:last-child{margin-bottom:0;}
.wv-top{display:inline-block;margin-top:26px;font-size:.82rem;font-weight:700;color:var(--wv-accent-ink);text-decoration:none;}
.wv-top:hover{text-decoration:underline;}
`;

export default function WalletvenPrivacy() {
  const [lang, setLang] = useState("es");
  const [active, setActive] = useState(SECTIONS[0].id);
  const t = UI[lang];

  useEffect(() => {
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [lang]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    setActive(id);
  };

  return (
    <div className="wv-root">
        <Navbar/>
      <style>{styles}</style>

      <header className="wv-header">
        <span className="wv-eyebrow">{t.docLabel}</span>
        <p className="wv-brand">{COMPANY.brandName}</p>
        <h1 className="wv-title">{t.title}</h1>
        <p className="wv-meta">
          {t.updated}: {COMPANY.effectiveDate}
        </p>
        <div className="wv-langbar" role="group" aria-label={t.langAria}>
          <button className="wv-langbtn" aria-pressed={lang === "es"} onClick={() => setLang("es")}>
            Español
          </button>
          <button className="wv-langbtn" aria-pressed={lang === "en"} onClick={() => setLang("en")}>
            English
          </button>
        </div>
      </header>

      <div className="wv-rule">
        <span />
      </div>

      <div className="wv-shell">
        <nav className="wv-toc" aria-label={t.toc}>
          <p className="wv-toc-title">{t.toc}</p>
          <ul className="wv-toc-list">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <button
                  className="wv-toc-link"
                  data-active={active === s.id}
                  onClick={() => go(s.id)}
                >
                  {s[lang].title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="wv-content">
          {SECTIONS.map((s, i) => (
            <section key={s.id} id={s.id} className="wv-section">
              <span className="wv-num">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="wv-h2">{s[lang].title}</h2>
              {s[lang].body.map((para, j) => (
                <p key={j} className="wv-p">
                  {fill(para)}
                </p>
              ))}
            </section>
          ))}
          <a
            className="wv-top"
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
              window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
            }}
          >
            ↑ {t.top}
          </a>
        </main>
      </div>
    </div>
  );
}